import React, { useState } from "react";
import { StyleSheet, View, Alert } from "react-native";
import * as Yup from "yup";

import Screen from "../components/Screen";
import { AppForm, AppFormField, SubmitButton } from "../components/forms";
import usersApi from "../api/users";
import authApi from "../api/auth";
import useApi from "../hooks/useApi";
import AppButton from "../components/AppButton";
import config from "../config/config";
import BlockSeperator from "../components/BlockSeperator";
import IconTouchable from "../components/IconTouchable";
import AppText from "../components/AppText";
import ProgressBar from "../components/ProgressBar";

const nameValidationSchema = Yup.object().shape({
    name: Yup.string().required().max(35).label("Name").min(3),
});

const emailValidationSchema = Yup.object().shape({
    email: Yup.string().required().email().label("Email"),
});

const passwordValidationSchema = Yup.object().shape({
    password: Yup.string().required().min(4).label("Password"),
    confirmPassword: Yup.string().oneOf([Yup.ref("password"), null], "Passwords must match"),
});

const phoneValidationSchema = Yup.object().shape({
    phone: Yup.number().min(2).required().label("Phone"),
});

export default function NewUserScreen({ navigation }) {
    const registerApi = useApi(usersApi.register);
    const loginApi = useApi(authApi.login);
    const [step, setStep] = useState(1);

    const [name, setName] = useState();
    const [email, setEmail] = useState();
    const [phone, setPhone] = useState();
    const [password, setPassword] = useState();
    const [type, setType] = useState();
    const [message, setMessage] = useState(false);

    const handleSubmit = async () => {
        const userInfo = {
            name: name.name,
            email: email.email,
            phone: phone.phone,
            password: password.password,
            type: type,
        };

        const response = await registerApi.request(userInfo);
        if (response.ok) {
            setMessage("User Created");
        } else {
            setMessage("Unable to Create User");
        }
        resetProgress(1);
    };

    const resetProgress = async () => {
        setStep(1);
    };

    return (
        <Screen title={"New User"} navigation={navigation} message={message} setMessage={setMessage}>
            <ProgressBar height={30} steps={5} step={step} />

            <BlockSeperator />
            <View style={styles.container}>
                {step === 1 && (
                    <View>
                        <AppForm
                            initialValues={{
                                name: "",
                            }}
                            onSubmit={(name) => {
                                setName(name);
                                setStep(step + 1);
                            }}
                            validationSchema={nameValidationSchema}
                        >
                            <AppFormField autoCorrect={false} icon="account" name="name" placeholder="Enter Name" textContentType="name" autoFocus={true} />
                            <SubmitButton title="Next" />
                        </AppForm>
                    </View>
                )}
                {step === 2 && (
                    <View>
                        <AppForm
                            initialValues={{
                                email: "",
                            }}
                            onSubmit={(email) => {
                                setEmail(email);
                                setStep(step + 1);
                            }}
                            validationSchema={emailValidationSchema}
                        >
                            <AppFormField
                                autoCapitalize="none"
                                autoCorrect={false}
                                icon="email"
                                keyboardType="email-address"
                                name="email"
                                placeholder="Email"
                                textContentType="emailAddress"
                                autoFocus={true}
                            />
                            <SubmitButton title="Next" />
                        </AppForm>
                    </View>
                )}
                {step === 3 && (
                    <View>
                        <AppForm
                            initialValues={{
                                phone: "",
                            }}
                            onSubmit={(phone) => {
                                setPhone(phone);
                                setStep(step + 1);
                            }}
                            validationSchema={phoneValidationSchema}
                        >
                            <AppFormField autoCapitalize="none" autoCorrect={false} icon="phone" name="phone" placeholder="Phone" autoFocus={true} />
                            <SubmitButton title="Next" />
                        </AppForm>
                    </View>
                )}
                {step === 4 && (
                    <View>
                        <AppForm
                            initialValues={{
                                password: "",
                                confirmPassword: "",
                            }}
                            onSubmit={(password) => {
                                setPassword(password);
                                setStep(step + 1);
                            }}
                            validationSchema={passwordValidationSchema}
                        >
                            <AppFormField
                                autoCapitalize="none"
                                autoCorrect={false}
                                icon="lock"
                                name="password"
                                placeholder="Password"
                                secureTextEntry
                                textContentType="password"
                                autoFocus={true}
                            />
                            <AppFormField
                                autoCapitalize="none"
                                autoCorrect={false}
                                icon="lock"
                                name="confirmPassword"
                                placeholder="Confirm Password"
                                secureTextEntry
                                textContentType="password"
                            />
                            <SubmitButton title="Next" />
                        </AppForm>
                    </View>
                )}
                {step === 5 && (
                    <View>
                        <View style={{ alignItems: "center", paddingBottom: 50 }}>
                            <AppText style={config.text}>{"Select User Type"}</AppText>
                        </View>
                        <View style={{ flexDirection: "row", justifyContent: "space-around", paddingBottom: 40 }}>
                            <IconTouchable
                                name="crane"
                                text="Yard"
                                onPress={() => {
                                    setType("yard");
                                    setStep(step + 1);
                                }}
                            />
                            <IconTouchable
                                name="account-star"
                                text="Admin"
                                onPress={() => {
                                    setType("admin");
                                    setStep(step + 1);
                                }}
                                backgroundColor={config.colors.red}
                            />
                        </View>
                    </View>
                )}
                {step === 6 && <AppButton title="Add User" color={config.colors.tertiary} borderColor={config.colors.green} onPress={handleSubmit} />}
            </View>
        </Screen>
    );
}

const styles = StyleSheet.create({
    container: {
        padding: 20,
        paddingTop: 30,
    },
});
